import React, { useEffect } from 'react';
import { preventImageProtection } from '../../utils/imageProtection';
import { useTranslation } from '../../hooks/useTranslation';
import { encUrl } from '../../utils/encUrl';
import { photoPath } from '../../utils/collections';
import EncryptedImage from '../EncryptedImage/EncryptedImage';
import styles from './Lightbox.module.css';

const Lightbox = ({ images, currentIndex, onClose, onPrevious, onNext }) => {
  const { t } = useTranslation();

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') onPrevious();
      if (e.key === 'ArrowRight') onNext();
    };

    // Bloquer le scroll du body quand la lightbox est ouverte
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', handleKeyDown);

    return () => {
      document.body.style.overflow = 'unset';
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [onClose, onPrevious, onNext]);

  // Note: adjacent-image preloading removed — encrypted blobs require fetch+decrypt
  // and cannot use new Image(). Browser cache covers most latency after initial gallery load.

  const currentImage = images[currentIndex];

  return (
    <div className={styles.lightbox} onClick={onClose}>
      <div className={styles.content} onClick={(e) => e.stopPropagation()}>
        {/* Bouton fermer */}
        <button
          className={styles.closeButton}
          onClick={onClose}
          aria-label={t('lightbox.close')}
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>

        {/* Bouton précédent */}
        {images.length > 1 && (
          <button
            className={`${styles.navButton} ${styles.prevButton}`}
            onClick={onPrevious}
            aria-label={t('lightbox.previous')}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="15 18 9 12 15 6" />
            </svg>
          </button>
        )}

        {/* Image avec watermark */}
        <div className={styles.imageContainer}>
          <div className={styles.imageWrapper}>
            <EncryptedImage
              src={encUrl(photoPath(currentImage))}
              alt={currentImage.alt}
              className={styles.image}
              ratio={currentImage.ratio}
              onContextMenu={preventImageProtection}
              onDragStart={preventImageProtection}
            />
            <div className={styles.watermark}>© Antoine Pierson</div>
          </div>
        </div>

        {/* Informations */}
        {currentImage.title && (
          <div className={styles.info}>
            <p className={styles.title}>{currentImage.title}</p>
            <p className={styles.counter}>
              {currentIndex + 1} / {images.length}
            </p>
          </div>
        )}

        {/* Bouton suivant */}
        {images.length > 1 && (
          <button
            className={`${styles.navButton} ${styles.nextButton}`}
            onClick={onNext}
            aria-label={t('lightbox.next')}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="9 18 15 12 9 6" />
            </svg>
          </button>
        )}
      </div>
    </div>
  );
};

export default Lightbox;
